import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import rolePermissionService from "../../../services/rolePermissionService";
import { instanceToken } from "../../../utils/constant";
import {
  FALLBACK_CATALOG,
  getAssignableCatalog,
} from "../../../config/rolePermissions";

const roleCodeHint =
  "Uppercase letters, digits, underscore; 2-32 chars; must start with a letter";

export default function RolePermissions({ homeAlert }) {
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);
  const [deletingRole, setDeletingRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [roleList, setRoleList] = useState([]);
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [error, setError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createCode, setCreateCode] = useState("");
  const [createModules, setCreateModules] = useState([]);
  const [creating, setCreating] = useState(false);

  const activeCatalog = useMemo(
    () =>
      (Array.isArray(catalog) ? catalog : FALLBACK_CATALOG).filter(
        (m) => m.active !== false
      ),
    [catalog]
  );

  const moduleColumns = useMemo(
    () =>
      activeCatalog.map((m) => ({
        key: m.key,
        label: m.label || m.key,
        section: m.section || "",
        adminOnly: Boolean(m.adminOnly),
      })),
    [activeCatalog]
  );

  const editableRoles = roleList.filter((r) => r.role !== "ADMIN");

  const renderTypeChip = (system) =>
    system ? (
      <Chip label="System" size="small" variant="outlined" />
    ) : (
      <Chip label="Custom" size="small" color="secondary" variant="outlined" />
    );

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await rolePermissionService.getAll(instanceToken.token);
      setPermissions(res.data.permissions || {});
      setRoleList(Array.isArray(res.data.roles) ? res.data.roles : []);
      if (Array.isArray(res.data.catalog) && res.data.catalog.length) {
        setCatalog(res.data.catalog);
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error || "Failed to load role permissions"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleModule = (role, moduleKey) => {
    setPermissions((prev) => {
      const current = Array.isArray(prev[role]) ? [...prev[role]] : [];
      const next = current.includes(moduleKey)
        ? current.filter((k) => k !== moduleKey)
        : [...current, moduleKey];
      return { ...prev, [role]: next };
    });
  };

  const toggleCreateModule = (moduleKey) => {
    setCreateModules((prev) =>
      prev.includes(moduleKey)
        ? prev.filter((k) => k !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  const saveRole = async (role) => {
    setSavingRole(role);
    try {
      const modules = Array.isArray(permissions[role])
        ? permissions[role]
        : [];
      const res = await rolePermissionService.updateRole(
        instanceToken.token,
        role,
        modules
      );
      setPermissions((prev) => ({
        ...prev,
        [role]: res.data.modules || modules,
      }));
      homeAlert?.(`${role} permissions updated`, false);
    } catch (err) {
      console.error(err);
      homeAlert?.(
        err?.response?.data?.error || `Failed to update ${role}`,
        true
      );
    } finally {
      setSavingRole(null);
    }
  };

  const handleCreateRole = async () => {
    const code = createCode.trim().toUpperCase();
    if (!code) {
      homeAlert?.("Role code is required", true);
      return;
    }
    setCreating(true);
    try {
      await rolePermissionService.createRole(
        instanceToken.token,
        code,
        createModules
      );
      homeAlert?.(`Role ${code} created`, false);
      setCreateOpen(false);
      setCreateCode("");
      setCreateModules([]);
      await load();
    } catch (err) {
      console.error(err);
      homeAlert?.(
        err?.response?.data?.error || "Failed to create role",
        true
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = async (role) => {
    if (
      !window.confirm(
        `Delete role "${role}"? This cannot be undone. Users must be reassigned first.`
      )
    ) {
      return;
    }
    setDeletingRole(role);
    try {
      await rolePermissionService.deleteRole(instanceToken.token, role);
      homeAlert?.(`Role ${role} deleted`, false);
      await load();
    } catch (err) {
      console.error(err);
      homeAlert?.(
        err?.response?.data?.error || `Failed to delete ${role}`,
        true
      );
    } finally {
      setDeletingRole(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Role Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create custom roles and toggle which dashboard modules each role can
            access. ADMIN always has full access and cannot be edited.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create Role
        </Button>
      </Box>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : null}

      <TableContainer component={Paper} sx={{ maxHeight: "75vh" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 90 }}>Type</TableCell>
              {moduleColumns.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  sx={{ fontWeight: 700, minWidth: 90, px: 1 }}
                >
                  <Typography variant="caption" display="block">
                    {col.section}
                  </Typography>
                  {col.label}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ bgcolor: "#f5f7fb" }}>
              <TableCell sx={{ fontWeight: 700 }}>ADMIN</TableCell>
              <TableCell>{renderTypeChip(true)}</TableCell>
              {moduleColumns.map((col) => (
                <TableCell key={col.key} align="center">
                  <Checkbox checked disabled />
                </TableCell>
              ))}
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  Locked
                </Typography>
              </TableCell>
            </TableRow>

            {editableRoles.map(({ role, system }) => {
              const selected = Array.isArray(permissions[role])
                ? permissions[role]
                : [];
              return (
                <TableRow key={role} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{role}</TableCell>
                  <TableCell>{renderTypeChip(system)}</TableCell>
                  {moduleColumns.map((col) => (
                    <TableCell key={col.key} align="center">
                      {col.adminOnly ? (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Checkbox
                          checked={selected.includes(col.key)}
                          onChange={() => toggleModule(role, col.key)}
                        />
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={savingRole === role}
                        onClick={() => saveRole(role)}
                      >
                        Save
                      </LoadingButton>
                      {!system ? (
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          color="error"
                          loading={deletingRole === role}
                          onClick={() => handleDeleteRole(role)}
                        >
                          Delete
                        </LoadingButton>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Create Custom Role</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Role Code"
              value={createCode}
              onChange={(e) => setCreateCode(e.target.value.toUpperCase())}
              helperText={roleCodeHint}
              placeholder="e.g. FINANCE"
              fullWidth
            />
            <Typography variant="subtitle2">Initial module access</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {getAssignableCatalog(catalog).map((col) => (
                <Chip
                  key={col.key}
                  label={col.label}
                  clickable
                  color={createModules.includes(col.key) ? "primary" : "default"}
                  onClick={() => toggleCreateModule(col.key)}
                  variant={
                    createModules.includes(col.key) ? "filled" : "outlined"
                  }
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={creating}
            onClick={handleCreateRole}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
